import React from 'react';
import { Check, CreditCard, FileText, Users, Building, User, Package } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StepItem {
  id: number;
  name: string;
  icon: React.ReactNode;
  path: string;
}

const steps: StepItem[] = [
  {
    id: 1,
    name: 'Cliente',
    icon: <User className="h-5 w-5" />,
    path: '/subscription-wizard/step1'
  },
  {
    id: 2,
    name: 'Conta',
    icon: <Building className="h-5 w-5" />,
    path: '/subscription-wizard/step2'
  },
  {
    id: 3,
    name: 'Contatos',
    icon: <Users className="h-5 w-5" />,
    path: '/subscription-wizard/step3'
  },
  {
    id: 4,
    name: 'Detalhes',
    icon: <FileText className="h-5 w-5" />,
    path: '/subscription-wizard/step4'
  },
  {
    id: 5,
    name: 'Planos',
    icon: <Package className="h-5 w-5" />,
    path: '/subscription-wizard/step5'
  },
  {
    id: 6,
    name: 'Resumo',
    icon: <CreditCard className="h-5 w-5" />,
    path: '/subscription-wizard/step6'
  }
];

interface SubscriptionWizardStepsProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
  className?: string;
}

const SubscriptionWizardSteps: React.FC<SubscriptionWizardStepsProps> = ({
  currentStep,
  onStepClick,
  className = ""
}) => {
  return (
    <div className={`mb-8 ${className}`}>
      <div className="hidden md:flex items-center justify-between">
        {steps.map((step, index) => {
          return (
            <div 
              key={step.id} 
              className="flex items-center"
              style={{ flex: index < steps.length - 1 ? 1 : 'none' }}
            >
              <div 
                className={cn(
                  "flex items-center",
                  onStepClick && currentStep > step.id ? "cursor-pointer" : ""
                )}
                onClick={() => {
                  if (onStepClick && currentStep > step.id) {
                    onStepClick(step.id);
                  }
                }}
                data-lov-id={step.id.toString()}
              >
                <div 
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full border-2",
                    currentStep === step.id 
                      ? "border-primary bg-primary text-primary-foreground" 
                      : currentStep > step.id 
                        ? "border-primary bg-primary/10 text-primary" 
                        : "border-muted-foreground/30 text-muted-foreground"
                  )}
                >
                  {currentStep > step.id ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    step.icon
                  )}
                </div>
                
                <span 
                  className={cn(
                    "ml-2 text-sm font-extrabold",
                    currentStep === step.id 
                      ? "text-primary" 
                      : currentStep > step.id 
                        ? "text-foreground" 
                        : "text-muted-foreground"
                  )}
                >
                  {step.name}
                </span>
              </div>
              
              {index < steps.length - 1 && (
                <div 
                  className={cn(
                    "flex-1 h-0.5 mx-4",
                    currentStep > index + 1 
                      ? "bg-primary" 
                      : "bg-muted-foreground/30"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Mobile view */}
      <div className="flex md:hidden overflow-x-auto pb-2 mb-2">
        <div className="flex space-x-4">
          {steps.map((step) => (
            <div 
              key={step.id}
              className={cn(
                "flex flex-col items-center justify-center min-w-[60px]",
                onStepClick && currentStep > step.id ? "cursor-pointer" : ""
              )}
              onClick={() => {
                if (onStepClick && currentStep > step.id) {
                  onStepClick(step.id);
                }
              }}
              data-lov-id={step.id.toString()}
            >
              <div 
                className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-full border-2 mb-1",
                  currentStep === step.id 
                    ? "border-primary bg-primary text-primary-foreground" 
                    : currentStep > step.id 
                      ? "border-primary bg-primary/10 text-primary" 
                      : "border-muted-foreground/30 text-muted-foreground"
                )}
              >
                {currentStep > step.id ? (
                  <Check className="h-5 w-5" />
                ) : (
                  step.icon
                )}
              </div>
              <span 
                className={cn(
                  "text-xs font-extrabold text-center",
                  currentStep === step.id 
                    ? "text-primary" 
                    : currentStep > step.id 
                      ? "text-foreground" 
                      : "text-muted-foreground"
                )}
              >
                {step.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionWizardSteps; 